import { useEffect, useState } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { appId, db } from '@/lib/firebase';

export interface DonorRollupTimestamp {
  toDate: () => Date;
}

export interface DonorRollupData {
  totalDonated: number;
  donationCount: number;
  monthlyDonationCount: number;
  oneTimeDonationCount: number;
  lastDonationAt: DonorRollupTimestamp | null;
  lastDonationAmount: number;
  lastDonationFrequency: 'monthly' | 'one_time' | null;
  currency: string;
}

interface UseDonorRollupResult {
  donorRecord: DonorRollupData | null;
  loading: boolean;
  loadError: string | null;
}

function mapDonorRollup(data: Record<string, unknown>): DonorRollupData {
  return {
    totalDonated: typeof data.totalDonated === 'number' ? data.totalDonated : 0,
    donationCount: typeof data.donationCount === 'number' ? data.donationCount : 0,
    monthlyDonationCount:
      typeof data.monthlyDonationCount === 'number' ? data.monthlyDonationCount : 0,
    oneTimeDonationCount:
      typeof data.oneTimeDonationCount === 'number' ? data.oneTimeDonationCount : 0,
    lastDonationAt: (data.lastDonationAt as DonorRollupTimestamp | null) ?? null,
    lastDonationAmount: typeof data.lastDonationAmount === 'number' ? data.lastDonationAmount : 0,
    lastDonationFrequency:
      data.lastDonationFrequency === 'monthly' || data.lastDonationFrequency === 'one_time'
        ? data.lastDonationFrequency
        : null,
    currency: typeof data.currency === 'string' ? data.currency : 'usd',
  };
}

/**
 * Reads donor rollup from `artifacts/{appId}/users/{userId}/donorRollup/main`
 * (path-scoped rules — works even when the doc does not exist yet).
 */
export function useDonorRollup(
  userId: string,
  logContext: string
): UseDonorRollupResult {
  const [donorRecord, setDonorRecord] = useState<DonorRollupData | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    const rollupRef = doc(db, 'artifacts', appId, 'users', userId, 'donorRollup', 'main');

    const unsubscribe = onSnapshot(
      rollupRef,
      (snapshot) => {
        if (!snapshot.exists()) {
          setDonorRecord(null);
          setLoading(false);
          setLoadError(null);
          return;
        }

        setDonorRecord(mapDonorRollup(snapshot.data()));
        setLoading(false);
        setLoadError(null);
      },
      (error) => {
        console.error(`[${logContext}] Failed to load donor rollup for user "${userId}":`, error);
        setDonorRecord(null);
        setLoadError('Unable to load your donor summary right now. Please refresh and try again.');
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [logContext, userId]);

  return { donorRecord, loading, loadError };
}
